// src/App.js
import React, { useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { MsalProvider, useMsal, MsalAuthenticationTemplate } from "@azure/msal-react";
import { EventType, InteractionType } from "@azure/msal-browser";

import TimeEntry from "./components/TimeEntry";
import Company from "./components/Company";
import Clients from "./components/Clients";
import Prospects from "./components/Prospects";
import Reports from "./components/Reports";
import ReportDesigner from "./components/ReportDesigner";
import Dashboard from "./components/Dashboard";
import ManageUsers from "./components/ManageUsers";
import Features from "./components/Features";
import Billing from "./components/Billing";
import Approvals from "./components/Approvals";
import AuditHistory from "./components/AuditHistory";
import JobTypes from "./components/JobTypes";
import ExpenseTypes from "./components/ExpenseTypes";
import BillingRoles from "./components/BillingRoles";
import Visibility from "./components/Visibility";
import SettingsLayout from "./components/SettingsLayout";
import PageLayout from "./components/Layout";
import EditProfile from "./components/EditProfile";
import Sandbox from "./components/Sandbox";
import DevTierPreview from "./components/DevTierPreview";

import Printer from "./reporting/Printer";
import PdfExport from "./reporting/PdfExport";

import { IdentityScope, UserProvider } from "./utils/userContext";
import { TierProvider } from "./utils/tierContext";
import { loginRequest } from "./authConfig";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

import "./App.css";
import "./assets/css/style.css";
import "./assets/css/responsive.css";
import "./assets/css/animate.css";

import ErrorBoundary from "./components/ErrorBoundary";
import RequireRole from "./utils/requireRole";
import { Roles } from "./utils/access";

import RequireTenant from "./utils/requireTenant";
import SelectTenant from "./components/SelectTenant";
import InviteAccept from "./components/InviteAccept";
import RegisterRedirect from "./components/RegisterRedirect";
import Signup from "./components/Signup";
import {
  PrivacyPage,
  SecurityPage,
  SubprocessorsPage,
  TermsPage,
} from "./components/LegalPages";
import FeatureGate from "./utils/featureGate";
import RequireTier from "./utils/requireTier";
import LegalAcceptanceGate from "./components/LegalAcceptanceGate";
import { useUser } from "./utils/userContext";
import { useTier } from "./utils/tierContext";
import WorkspaceLoading from "./components/WorkspaceLoading";

// Dev drawer (lazy, development or explicitly enabled staging builds only)
const DevDrawer = (process.env.NODE_ENV === "development" || process.env.REACT_APP_ENABLE_E2E_OVERRIDES === "true")
  ? React.lazy(() => import("./components/DevDrawer"))
  : null;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const scrollContainer = document.querySelector('.column-2');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * Your "normal" authenticated app routes
 */
export const Pages = () => {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/selecttenant" element={<SelectTenant />} />

        {/* Dashboard — Pro/Enterprise feature, Owner + Admin only */}
        <Route
          path="/dashboard"
          element={
            <RequireRole allowedRoles={[Roles.Owner, Roles.Admin]}>
              <FeatureGate feature="dashboard" fallback={<Navigate to="/timeentry" replace />}>
                <Dashboard />
              </FeatureGate>
            </RequireRole>
          }
        />

        {/* Admin + Owner + PM */}
        <Route
          path="/clients"
          element={
            <RequireRole allowedRoles={[Roles.Owner, Roles.Admin, Roles.ProjectManager]}>
              <FeatureGate feature="client_prospect_coding" fallback={<Navigate to="/timeentry" replace />}>
                <Clients />
              </FeatureGate>
            </RequireRole>
          }
        />
        <Route
          path="/prospects"
          element={
            <RequireRole allowedRoles={[Roles.Owner, Roles.Admin, Roles.ProjectManager]}>
              <FeatureGate feature="client_prospect_coding" fallback={<Navigate to="/timeentry" replace />}>
                <Prospects />
              </FeatureGate>
            </RequireRole>
          }
        />

        {/* Settings — Owner + Admin only */}
        <Route
          path="/settings"
          element={
            <RequireRole allowedRoles={[Roles.Owner, Roles.Admin]}>
              <SettingsLayout />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="/settings/company" replace />} />
          <Route path="company" element={<Company />} />
          <Route path="users" element={<ManageUsers />} />
          <Route path="billing" element={<RequireRole allowedRoles={[Roles.Owner]} redirectTo="/settings/company"><Billing /></RequireRole>} />
          <Route path="features" element={<Features />} />
          <Route path="jobtypes" element={<JobTypes />} />
          <Route path="expensetypes" element={<ExpenseTypes />} />
          <Route path="billingroles" element={<BillingRoles />} />
          <Route path="visibility" element={<RequireTier minTier="core" redirectTo="/settings/features"><Visibility /></RequireTier>} />
          <Route path="audit" element={<FeatureGate feature="audit_history" fallback={<Navigate to="/settings/company" replace />}><AuditHistory /></FeatureGate>} />
        </Route>

        {/* All authenticated roles */}
        <Route
          path="/approvals"
          element={
            <RequireRole allowedRoles={[Roles.Owner, Roles.Admin, Roles.ProjectManager, Roles.User]}>
              <FeatureGate feature="approval_workflow" fallback={<Navigate to="/timeentry" replace />}>
                <Approvals />
              </FeatureGate>
            </RequireRole>
          }
        />
        <Route
          path="/timeentry"
          element={
            <RequireRole allowedRoles={[Roles.Owner, Roles.Admin, Roles.ProjectManager, Roles.User]}>
              <TimeEntry />
            </RequireRole>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireRole allowedRoles={[Roles.Owner, Roles.Admin, Roles.ProjectManager, Roles.User]}>
              <Reports />
            </RequireRole>
          }
        />
        <Route
          path="/designer"
          element={
            <RequireRole allowedRoles={[Roles.Owner, Roles.Admin]}>
              <ReportDesigner />
            </RequireRole>
          }
        />

        {/* Any authenticated user (your app-level profile page) */}
        <Route path="/editprofile" element={<EditProfile />} />
        <Route path="/sandbox" element={<Sandbox />} />
        {process.env.NODE_ENV === "development" && (
          <Route path="/dev/tiers" element={<DevTierPreview />} />
        )}

        {/* Default */}
        <Route path="/" element={<DefaultLanding />} />
        <Route path="*" element={<DefaultLanding />} />
      </Routes>
    </>
  );
};

export function DefaultLanding() {
  const { user, loading } = useUser();
  const { hasFeature, loading: tierLoading } = useTier();
  if (loading || tierLoading) return <WorkspaceLoading />;
  const roleId = Number(user?.roleId);
  const canUseDashboard = (roleId === Roles.Owner || roleId === Roles.Admin) && hasFeature("dashboard");
  const target = canUseDashboard ? "/dashboard" : "/timeentry";
  return <Navigate to={target} replace />;
}

/**
 * Normal app shell (MSAL event callback + your layout/providers)
 *
 * With External ID (OTP/social), we do NOT need:
 * - edit-profile user-flow handling
 * - forgot-password flow handling
 * - AADB2C90118 logic
 */
const AppShell = () => {
  const { instance } = useMsal();

  useEffect(() => {
    const callbackId = instance.addEventCallback((event: any) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.payload?.account
      ) {
        // no-op (keep hook for future telemetry/diagnostics if you want)
      }

      if (event.eventType === EventType.LOGIN_FAILURE) {
        // no-op (B2C password reset error handling removed)
      }
    });

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  const authRequest = { ...loginRequest };

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
      <UserProvider>
        <IdentityScope>
        <TierProvider>
          <RequireTenant>
            <LegalAcceptanceGate>
              <PageLayout>
                <Pages />
              </PageLayout>
            </LegalAcceptanceGate>
          </RequireTenant>
          {DevDrawer && (
            <React.Suspense fallback={null}>
              <DevDrawer />
            </React.Suspense>
          )}
        </TierProvider>
        </IdentityScope>
      </UserProvider>
    </MsalAuthenticationTemplate>
  );
};

/**
 * Root router:
 * - /reports/print and /reports/pdf-export are PUBLIC (no layout/providers), so Playwright/PDF export won't be forced into auth.
 * - /invite/accept is PUBLIC (no tenant required to view/validate).
 * - /register is PUBLIC and starts the External ID account creation prompt.
 * - /signup/* is PUBLIC (bootstrap signup flow).
 * - /terms, /privacy, /subprocessors, and /security are PUBLIC legal/trust pages.
 * - everything else uses the normal AppShell.
 */
const RootRouter = () => {
  const location = useLocation();
  const isPrint = location.pathname === "/reports/print";
  const isPdfExport = location.pathname === "/reports/pdf-export";
  const isInviteAccept = location.pathname === "/invite/accept";
  const isRegister = location.pathname === "/register";
  const isSignup = location.pathname.startsWith("/signup");
  const isLegal = ["/terms", "/privacy", "/subprocessors", "/security"].includes(location.pathname);

  if (isPrint || isPdfExport || isInviteAccept || isRegister || isSignup || isLegal) {
    return (
      <Routes>
        <Route path="/reports/print" element={<Printer />} />
        <Route path="/reports/pdf-export" element={<PdfExport />} />
        <Route path="/invite/accept" element={<InviteAccept />} />
        <Route path="/register" element={<RegisterRedirect />} />
        <Route path="/signup/*" element={<Signup />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/subprocessors" element={<SubprocessorsPage />} />
        <Route path="/security" element={<SecurityPage />} />
      </Routes>
    );
  }

  return <AppShell />;
};

/**
 * App entry
 */
const App = ({ instance }: { instance: any }) => {
  return (
    <MsalProvider instance={instance}>
      <ErrorBoundary>
        <RootRouter />
      </ErrorBoundary>
    </MsalProvider>
  );
};

export default App;